<script setup lang="ts">
import { onUnmounted, provide, ref, toRef } from "vue";

import { dropdownKey } from "@/components/ui/DropdownMenu/injectKeys";
import MenuList from "@/components/ui/MenuList/MenuList.vue";
import MenuListItems from "@/components/ui/MenuList/MenuListItems.vue";
import { injectStrict } from "@/utils/vue";

import { moreMenuKey } from "../injectKeys";

// Hover waits a moment before acting, so passing over the list opens nothing and a diagonal move
// from a row into its flyout can cross other rows
const openDelay = 80;
const switchDelay = 120;
const closeDelay = 300;

const props = defineProps<{ host: HTMLElement | null }>();

const dropdown = injectStrict(dropdownKey);
const activeId = ref<string | null>(null);
let timer: ReturnType<typeof setTimeout> | undefined;

provide(moreMenuKey, {
  activeId,
  select,
  hover,
  leave,
  close: dropdown.close,
  host: toRef(props, "host"),
});

onUnmounted(() => clearTimeout(timer));

function select(id: string | null) {
  clearTimeout(timer);
  activeId.value = id;
}

function hover(event: PointerEvent, id: string | null) {
  if (!followsPointer(event)) return;

  if (id === activeId.value) {
    clearTimeout(timer);
  } else {
    selectLater(id, activeId.value === null ? openDelay : switchDelay);
  }
}

function leave(event: PointerEvent) {
  if (!followsPointer(event)) return;

  if (activeId.value === null) {
    clearTimeout(timer);
  } else {
    selectLater(null, closeDelay);
  }
}

function selectLater(id: string | null, delay: number) {
  clearTimeout(timer);
  timer = setTimeout(() => (activeId.value = id), delay);
}

// Keyboard navigation dispatches synthetic pointer events without a pointerType. Once the focus
// is in a flyout the user works there, and the pointer drifting over other rows must not close it.
function followsPointer(event: PointerEvent) {
  return event.pointerType === "mouse" && !props.host?.contains(document.activeElement);
}

// Escape steps back one level. Without the keyup, the DropdownMenu above listens on the document
// and would close the whole menu on the same keystroke.
function closeSubmenu(event: KeyboardEvent) {
  if (!activeId.value) return;

  select(null);
  event.stopPropagation();
}
</script>

<template>
  <!-- arrowing to another row closes a flyout the mouse opened, so it never sits beside the
       focused row. Capture: the list's keyboard navigation stops the event. -->
  <MenuList
    selectable
    @keydown.esc="closeSubmenu"
    @keyup.esc.stop
    @keydown.capture.up.down="select(null)"
    @pointerleave="leave"
  >
    <MenuListItems role="menu" :label="$t('label.actionMenu.more')">
      <slot />
    </MenuListItems>
  </MenuList>
</template>
